import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import {
  ChangeEvent,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FilterItem } from '../../api/_generated_';
import NetworkContainer from '../../components/NetworkContainer';
import PurchasePriceFilters from '../../components/PurchasePriceFilters';
import PurchasePricesHeader from '../../components/PurchasePricesHeader';
import PurchasePriceTable from '../../components/PurchasePriceTable';
import {
  HttpRequestStatus, PurchasePriceFilterProps,
} from '../../components/types/types';
import ViewHeader from '../../components/ViewHeader';
import AppRoutes from '../../constants/routes';
import useDebounce from '../../hooks/useDebounce';
import usePurchasePrices from '../../hooks/usePurchasePrices';
import { DATE_FORMAT } from '../../i18n/config';

export default function PurchasePriceReports() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target?.value);
  };

  const filters: PurchasePriceFilterProps = {
    search,
  };

  const { data, status } = usePurchasePrices({
    search: debouncedSearch,
  });

  const depositOptions: FilterItem[] = [
    { label: '', value: null as any, sortOrder: 0 },
    ...data?.depositOptions || [],
  ];

  const deadline = dayjs(data?.deadline);

  const isDeadlineExpired = (!data?.deadline || deadline.isBefore(dayjs(), 'day'));

  return (
    <NetworkContainer status={status as HttpRequestStatus} containerSize="lg">
      <ViewHeader
        title={t('purchase-prices.title-purchase-price-reporting')}
        description={isDeadlineExpired
          ? t('purchase-prices.message-deadline-expired')
          : t('purchase-prices.label-purchase-price-reporting-desc')}
      >
        {!isDeadlineExpired && (
          <Button
            icon="pi pi-bars"
            label={t('purchase-prices.cmd-report-purchase-prices')}
            size="small"
            onClick={() => navigate(`${AppRoutes.PURCHASE_PRICE_REPORT_NEW}`)}
          />
        )}
      </ViewHeader>
      { !isDeadlineExpired && (
        <PurchasePricesHeader
          period={data?.period}
          deadline={deadline.format(DATE_FORMAT)}
          className="mb-6"
        />
      )}
      <PurchasePriceFilters
        filters={filters}
        onChangeSearch={handleChangeSearch}
      />
      <PurchasePriceTable
        items={data?.items}
        totalRecords={data?.totalRecords || 0}
        depositOptions={depositOptions}
      />
    </NetworkContainer>
  );
}
